import { Router } from '@angular/router';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { FilterItemSearch } from '../../../shared/ui/shared-filters/filter-item-search/filter-item-search';
import { FilterMultiSelectComponent } from '../../../shared/ui/shared-filters/filter-multi-select-component/filter-multi-select-component';
import { FilterDateComponent } from '../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSortingComponent } from '../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { SubmitButton } from '../../../shared/ui/Buttons/submit-button/submit-button';
import {
  validateInvoiceDates,
  validateInvoiceIds,
} from '../../admin-dashboard-component/invoice-list-component/invoice-list-component.validation';
import { CardHeaderItem, CardList } from './shared/card-list/card-list';
import { ErrorData } from '../../../utils/error-data/error-data';
import { Loading } from '../../../utils/loading/loading';
import { Pagination } from '../../../shared/ui/shared-tables/pagination/pagination';
import {
  Invoice,
  Invoicep,
  Invoices,
  InvoicesError,
  InvoiceStatus,
} from '../../../pages/admin-dashboard/admin-invoice/admin-invoice.model';
import { InvoiceService } from '../../../services/invoices/invoices.service';
import { PharmacyCreditService } from '../../../services/pharmacy-credit/pharmacy-credit';
import { PharmacyService } from '../../../services/pharmacy/pharmacy.service';
import { LookupVTO } from '../../../pages/admin-dashboard/admin-companies/admin-companies.model';

const INVOICE_STATUS_DOMAIN = 5901;

@Component({
  selector: 'app-invoice-list-pharmacist-component',
  imports: [
    FilterItemSearch,
    FilterMultiSelectComponent,
    FilterDateComponent,
    FilterSortingComponent,
    SubmitButton,
    CardList,
    ErrorData,
    Loading,
    Pagination,
  ],
  templateUrl: './invoice-list-pharmacist-component.html',
  styleUrl: './invoice-list-pharmacist-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceListPharmacistComponent {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private invoiceService = inject(InvoiceService);
  private pharmacyCreditService = inject(PharmacyCreditService);
  private pharmacyService = inject(PharmacyService);
  private destroyRef = inject(DestroyRef);

  selectedPharmacy = computed(() => this.pharmacyCreditService.selectedPharmacy());

  constructor() {
    this.loadInvoiceStatuses();

    effect(() => {
      const pharmacy = this.selectedPharmacy();

      if (!pharmacy) {
        return;
      }

      this.currentPage.set(1);
      this.loadInvoices();
    });
  }

  isArabic = computed(() => this.locale.startsWith('ar'));

  loading = signal(false);

  errorMessage2 = signal<string | null>(null);

  invoiceIdFilter = $localize`:@@invoiceIdFilter:Invoice ID`;

  invoiceIdPlaceholder = $localize`:@@invoiceIdPlaceholder:Search by Invoice ID`;

  orderIdFilter = $localize`:@@orderIdFilter:Order ID`;

  orderIdPlaceholder = $localize`:@@orderIdPlaceholder:Search by Order ID`;

  invoiceDateFrom = $localize`:@@invoiceDateFrom:Invoice Date From`;
  invoiceDateTo = $localize`:@@invoiceDateTo:Invoice Date To`;

  statusFilter = {
    titleEn: 'Status',
    titleAr: 'الحالة',
  };

  allStatuses = {
    titleEn: 'Select Status',
    titleAr: 'حدد الحالة',
  };

  invoiceStatuses = signal<LookupVTO[]>([]);

  statusOptions = computed(() =>
    this.invoiceStatuses().map((s) => ({
      id: s.id.toString(),
      titleEn: s.titleEn,
      titleAr: s.titleAr ?? '',
    })),
  );

  selectedStatuses = signal<string[]>([]);
  showStatusDropdown = signal(false);

  loadInvoiceStatuses() {
    const subscription = this.pharmacyService.getAllStatuses(INVOICE_STATUS_DOMAIN).subscribe({
      next: (res) => this.invoiceStatuses.set(res.list ?? []),
      error: (err) => console.error('Failed to load invoice statuses', err),
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  toggleStatusDropdown() {
    this.showStatusDropdown.update((value) => !value);
  }

  onStatusChange(statuses: string[]) {
    this.selectedStatuses.set(statuses);
  }

  invoiceSearch = signal('');
  orderSearch = signal('');

  invoiceIdError = signal<string | null>(null);
  orderIdError = signal<string | null>(null);

  validateIds() {
    const result = validateInvoiceIds(this.invoiceSearch().trim(), this.orderSearch().trim(), this.isArabic());

    this.invoiceIdError.set(result.invoiceIdError);
    this.orderIdError.set(result.orderIdError);

    return result.isValid;
  }

  invoiceFrom = signal<string | null>(null);
  invoiceTo = signal<string | null>(null);

  fromDateError = signal<string | null>(null);
  toDateError = signal<string | null>(null);

  validateDates() {
    const result = validateInvoiceDates(this.invoiceFrom(), this.invoiceTo(), this.isArabic());

    this.fromDateError.set(result.fromDateError);
    this.toDateError.set(result.toDateError);

    return result.isValid;
  }

  onFromDateChange(value: string | null) {
    this.invoiceFrom.set(value);
    this.validateDates();
  }

  onToDateChange(value: string | null) {
    this.invoiceTo.set(value);
    this.validateDates();
  }

  orderBy = signal('INVOICE_DATE');

  orderDir = signal<'ASC' | 'DESC'>('DESC');

  sortOptions = [
    {
      value: 'INVOICE_DATE',
      labelEn: 'Invoice Date',
      labelAr: 'تاريخ الفاتورة',
    },
    {
      value: 'TOTAL_PHARMACY_PRICE',
      labelEn: 'Total Pharmacy Price',
      labelAr: 'إجمالي سعر الصيدلية',
    },
    {
      value: 'STATUS',
      labelEn: 'Status',
      labelAr: 'الحالة',
    },
  ];

  toggleSortOrder() {
    this.orderDir.update((dir) => (dir === 'DESC' ? 'ASC' : 'DESC'));
  }

  applyFilters() {
    const idsValid = this.validateIds();
    const datesValid = this.validateDates();
    if (!idsValid || !datesValid) {
      return;
    }
    this.currentPage.set(1);

    console.log('Applying filters:', {
      invoiceId: this.invoiceSearch(),
      orderId: this.orderSearch(),
      invoiceFrom: this.invoiceFrom(),
      invoiceTo: this.invoiceTo(),
      statuses: this.selectedStatuses(),
      orderBy: this.orderBy(),
      orderDir: this.orderDir(),
    });

    this.loadInvoices();
  }

  resetFilters() {
    this.invoiceSearch.set('');
    this.orderSearch.set('');

    this.invoiceIdError.set(null);
    this.orderIdError.set(null);

    this.invoiceFrom.set(null);
    this.invoiceTo.set(null);

    this.fromDateError.set(null);
    this.toDateError.set(null);

    this.selectedStatuses.set([]);

    this.orderBy.set('INVOICE_DATE');
    this.orderDir.set('DESC');

    this.currentPage.set(1);

    this.loadInvoices();

    console.log('Filters reset');
  }

  headerItems = signal<CardHeaderItem[]>([
    {
      key: 'id',
      labelAr: 'رقم المعاملة',
      labelEn: 'Transaction ID',
      type: 'text',
      position: 'header',
    },
    {
      key: 'orderNumber',
      labelAr: 'رقم الطلب',
      labelEn: 'Order Number',
      type: 'text',
      position: 'body',
    },
    {
      key: 'status',
      labelAr: 'الحالة',
      labelEn: 'Status',
      type: 'status',
      position: 'header',
    },
    {
      key: 'createdOn',
      labelAr: 'تاريخ الإنشاء',
      labelEn: 'Created On',
      type: 'date',
      position: 'footer',
    },
    {
      key: 'createdBy',
      labelAr: 'بواسطة',
      labelEn: 'Created By',
      type: 'text',
      position: 'footer',
    },
    {
      key: 'amount1',
      labelAr: 'إجمالي الصيدلية',
      labelEn: 'Pharmacy Total',
      type: 'currency',
      position: 'footer',
    },
    {
      key: 'amount2',
      labelAr: 'إجمالي الأشخاص',
      labelEn: 'People Total',
      type: 'currency',
      position: 'footer',
    },
    {
      key: 'actions',
      labelAr: 'الإجراءات',
      labelEn: 'Actions',
      type: 'actions',
      position: 'header',
    },
  ]);

  emptyContentTitle = {
    ar: 'لا توجد بيانات متاحة',
    en: 'No data available',
  };

  emptyFilterTitle = {
    ar: 'لا توجد نتائج تطابق الفلتر',
    en: 'No results match the applied filters',
  };

  actions = signal([
    {
      id: 1,
      titleEn: 'View Invoice Details',
      titleAr: 'عرض تفاصيل الفاتورة',
    },
  ]);

  invoices = signal<Invoices[]>([]);
  totalItems = signal(0);

  loadInvoices() {
    const pharmacyId = this.selectedPharmacy()?.id;
    if (!pharmacyId) {
      return;
    }
    const idsValid = this.validateIds();
    const datesValid = this.validateDates();
    if (!idsValid || !datesValid) {
      return;
    }

    this.loading.set(true);
    this.errorMessage2.set(null);

    const invoiceIdValue = this.invoiceSearch().trim();
    const orderIdValue = this.orderSearch().trim();

    const firstStatusId = this.selectedStatuses()[0];
    const requestStatusId = firstStatusId ? Number(firstStatusId) : undefined;

    const subscription = this.invoiceService
      .getInvoicesByFilters(
        invoiceIdValue ? Number(invoiceIdValue) : undefined,
        orderIdValue ? Number(orderIdValue) : undefined,
        pharmacyId,
        undefined,
        requestStatusId,
        this.invoiceFrom() || undefined,
        this.invoiceTo() || undefined,
        this.orderBy(),
        this.orderDir(),
        this.currentPage() - 1,
        this.itemsPerPage(),
      )
      .subscribe({
        next: (response) => {
          this.invoices.set(response.list);
          this.totalItems.set(response.total);

          this.loading.set(false);
        },

        error: (error: InvoicesError) => {
          this.loading.set(false);

          this.errorMessage2.set(
            this.isArabic()
              ? error?.messageAr || 'حدث خطأ أثناء جلب الفواتير'
              : error?.messageEn || 'An error occurred while loading invoices',
          );
        },
      });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  tableRows = computed(() => {
    return this.invoices().map((invoice) => ({
      id: invoice.id,
      orderNumber: invoice.orderId,
      status: {
        titleEn: invoice.invoiceStatus.titleEn,
        titleAr: invoice.invoiceStatus.titleAr,
      },
      createdOn: invoice.createdOn,
      createdBy: invoice.createdBy.fullName,
      amount1: invoice.totalStorePrice,
      amount2: invoice.totalPeoplePrice,
    }));
  });

  handleAction(event: any) {
    if (event.actionId === 1) {
      const invoiceId = event.entity?.rawId ?? event.entity?.id;
      this.router.navigate(['/user/invoices/invoice-details', invoiceId]);
    }
  }

  isFilterActive = computed(() => {
    return (
      !!this.invoiceSearch() ||
      !!this.orderSearch() ||
      !!this.invoiceFrom() ||
      !!this.invoiceTo() ||
      this.selectedStatuses().length > 0
    );
  });

  itemsPerPage = signal(25);

  currentPage = signal(1);

  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage())));

  startItem = computed(() => {
    if (this.totalItems() === 0) {
      return 0;
    }

    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endItem = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems()));

  getPageNumbers() {
    return Array.from({ length: this.totalPages() }, (_, index) => index + 1);
  }

  onPreviousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((page) => page - 1);
    }
    this.loadInvoices();
  }

  onNextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((page) => page + 1);
    }
    this.loadInvoices();
  }

  onGoToPage(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
    this.loadInvoices();
  }

  onChangePageNumber(page: number) {
    this.onGoToPage(page);
  }

  onPageSizeChange() {
    this.currentPage.set(1);
  }
}
